import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner, Badge, Pagination, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { qscAPI } from '../utils/api';
import QscScoreBadge from '../components/QscScoreBadge';
import QscGradeBadge from '../components/QscGradeBadge';

const PAGE_SIZE = 10;

const QscList = () => {
  const { isSuperuser } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAssessments();
    }
  }, []);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const res = await qscAPI.getAssessments();
      const sorted = [...res.data.data].sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));
      setAssessments(sorted);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await qscAPI.deleteAssessment(deleteTarget.id);
      setDeleteTarget(null);
      hasLoadedRef.current = false;
      await loadAssessments();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบ');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'completed') return <Badge bg="success">เสร็จสิ้น</Badge>;
    return <Badge bg="warning" text="dark">ร่าง</Badge>;
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2><i className="bi bi-clipboard-check me-2"></i>ระบบประเมิน QSC</h2>
        </Col>
        {isSuperuser() && (
          <Col xs="auto">
            <Button as={Link} to="/qsc/new" variant="primary">
              <i className="bi bi-plus-circle me-2"></i>สร้างแบบประเมินใหม่
            </Button>
            {' '}
            <Button as={Link} to="/qsc/improvements" variant="outline-secondary">
              <i className="bi bi-images me-2"></i>รีวิวการแก้ไข
            </Button>
          </Col>
        )}
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            {assessments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-clipboard-x fs-1 d-block mb-2"></i>
                ยังไม่มีแบบประเมิน
              </div>
            ) : (() => {
              const totalPages = Math.ceil(assessments.length / PAGE_SIZE);
              const pageItems = assessments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
              return (
                <>
                  <Table hover responsive className="mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>วันที่ตรวจ</th>
                        {isSuperuser() && <th>สาขา</th>}
                        <th>คะแนนแยกหมวด</th>
                        <th className="text-center">รวม</th>
                        <th className="text-center">เกรด</th>
                        <th className="text-center">ผล</th>
                        <th className="text-center">การแก้ไข</th>
                        <th className="text-center">สถานะ</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map(a => {
                        const disabledIds = (a.disabledCategoryIds || []).map(Number);
                        const sortedScores = [...(a.categoryScores || [])]
                          .filter(cs => !disabledIds.includes(cs.categoryId))
                          .sort((x, y) => (x.category?.sortOrder || 0) - (y.category?.sortOrder || 0));
                        return (
                          <tr key={a.id}>
                            <td>{a.visitDate}</td>
                            {isSuperuser() && (
                              <td>{a.branch?.fullName || a.branch?.username || '-'}</td>
                            )}
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {sortedScores.map(cs => (
                                  <span key={cs.id} title={cs.category?.name}>
                                    <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.75rem' }}>
                                      {cs.category?.name}: <QscScoreBadge score={cs.score} small />
                                    </Badge>
                                  </span>
                                ))}
                                {sortedScores.length === 0 && <span className="text-muted">-</span>}
                              </div>
                            </td>
                            <td className="text-center">
                              {a.overallScore != null ? <QscScoreBadge score={a.overallScore} /> : '-'}
                            </td>
                            <td className="text-center">
                              {a.grade ? <QscGradeBadge grade={a.grade} /> : '-'}
                            </td>
                            <td className="text-center">
                              {a.isPass != null ? (
                                <Badge bg={a.isPass ? 'success' : 'danger'}>
                                  {a.isPass ? 'ผ่าน' : 'ไม่ผ่าน'}
                                </Badge>
                              ) : '-'}
                            </td>
                            <td className="text-center">
                              {a.failedCount === 0 ? (
                                <span className="text-muted small">-</span>
                              ) : a.allFixed ? (
                                <Badge bg="success">
                                  <i className="bi bi-check-all me-1"></i>แก้ไขแล้ว
                                </Badge>
                              ) : (
                                <Badge bg="warning" text="dark">
                                  {a.approvedImprovementCount}/{a.failedCount}
                                </Badge>
                              )}
                            </td>
                            <td className="text-center">{statusBadge(a.status)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button as={Link} to={`/qsc/${a.id}`} variant="outline-primary" size="sm">
                                  <i className="bi bi-eye me-1"></i>ดูรายละเอียด
                                </Button>
                                {isSuperuser() && (
                                  <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(a)}>
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
                      <div className="text-muted small">
                        แสดง {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, assessments.length)} จาก {assessments.length} รายการ
                      </div>
                      <Pagination className="mb-0" size="sm">
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
                      </Pagination>
                    </div>
                  )}
                </>
              );
            })()}
          </Card.Body>
        </Card>
      )}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>ยืนยันการลบ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>คุณต้องการลบแบบประเมินวันที่ <strong>{deleteTarget?.visitDate}</strong>
            {deleteTarget?.branch && <> สาขา <strong>{deleteTarget.branch?.fullName || deleteTarget.branch?.username}</strong></>} ใช่หรือไม่?</p>
          <p className="text-danger small mb-0">การลบจะไม่สามารถย้อนกลับได้ และจะลบข้อมูลการแก้ไขทั้งหมดที่เกี่ยวข้องด้วย</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>ยกเลิก</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" /> : <><i className="bi bi-trash me-1"></i>ลบแบบประเมิน</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default QscList;
